import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer, Legend, Area, AreaChart,
} from 'recharts';

// ─── Simplified Greeks calculations ────────────────────────────────

// Cumulative normal distribution approximation
function cdf(x: number): number {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429;
  const p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  const t = 1 / (1 + p * Math.abs(x));
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x / 2);
  return 0.5 * (1 + sign * y);
}

function pdf(x: number): number {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

function bsGreeks(S: number, K: number, T: number, r: number, sigma: number, type: 'call' | 'put') {
  if (T <= 0.001) T = 0.001;
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;

  let delta: number, gamma: number, theta: number, vega: number, price: number;

  gamma = pdf(d1) / (S * sigma * sqrtT);
  vega = S * pdf(d1) * sqrtT / 100; // per 1% vol change

  if (type === 'call') {
    delta = cdf(d1);
    theta = (-(S * pdf(d1) * sigma) / (2 * sqrtT) - r * K * Math.exp(-r * T) * cdf(d2)) / 365;
    price = S * cdf(d1) - K * Math.exp(-r * T) * cdf(d2);
  } else {
    delta = cdf(d1) - 1;
    theta = (-(S * pdf(d1) * sigma) / (2 * sqrtT) + r * K * Math.exp(-r * T) * cdf(-d2)) / 365;
    price = K * Math.exp(-r * T) * cdf(-d2) - S * cdf(-d1);
  }

  return { delta, gamma, theta, vega, price, d1, d2 };
}

// ─── Data generators ───────────────────────────────────────────────

function greeksVsSpot(K: number, T: number, vol: number, type: 'call' | 'put') {
  const data = [];
  const lo = Math.round(K * 0.7);
  const hi = Math.round(K * 1.3);
  for (let S = lo; S <= hi; S++) {
    const g = bsGreeks(S, K, T, 0.03, vol, type);
    data.push({
      price: S,
      delta: Math.round(g.delta * 1000) / 1000,
      gamma: Math.round(g.gamma * 10000) / 10000,
      theta: Math.round(g.theta * 100) / 100,
      vega: Math.round(g.vega * 100) / 100,
      premium: Math.round(g.price * 100) / 100,
    });
  }
  return data;
}

function greeksVsTime(S: number, K: number, vol: number, type: 'call' | 'put') {
  const data = [];
  for (let d = 180; d >= 1; d -= 2) {
    const T = d / 365;
    const g = bsGreeks(S, K, T, 0.03, vol, type);
    data.push({
      days: d,
      delta: Math.round(g.delta * 1000) / 1000,
      gamma: Math.round(g.gamma * 10000) / 10000,
      theta: Math.round(g.theta * 100) / 100,
      vega: Math.round(g.vega * 100) / 100,
      premium: Math.round(g.price * 100) / 100,
    });
  }
  return data;
}

function greeksVsVol(S: number, K: number, T: number, type: 'call' | 'put') {
  const data = [];
  for (let iv = 5; iv <= 80; iv += 1) {
    const g = bsGreeks(S, K, T, 0.03, iv / 100, type);
    data.push({
      iv,
      delta: Math.round(g.delta * 1000) / 1000,
      gamma: Math.round(g.gamma * 10000) / 10000,
      theta: Math.round(g.theta * 100) / 100,
      vega: Math.round(g.vega * 100) / 100,
      premium: Math.round(g.price * 100) / 100,
    });
  }
  return data;
}

// ─── Slider helper ─────────────────────────────────────────────────

function GSlider({ label, value, min, max, step, onChange, suffix = '' }: {
  label: string; value: number; min: number; max: number; step: number; onChange: (v: number) => void; suffix?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <span className="text-xs font-mono font-bold text-primary">{value}{suffix}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} />
    </div>
  );
}

// ─── Greek info cards ──────────────────────────────────────────────

const greekInfo = [
  {
    id: 'delta',
    name: 'Delta (Δ)',
    symbol: 'Δ',
    range: 'Put: -1 to 0 | Call: 0 to 1',
    definition: 'Rate of change of option price per $1 move in the underlying stock.',
    interpretation: 'A delta of -0.50 means the put gains $0.50 for every $1 the stock drops. Also approximates the probability the option expires in-the-money.',
    hedgeUse: 'Delta-neutral portfolios combine positions so total delta ≈ 0, making the portfolio immune to small price moves.',
    color: '#3b82f6',
    bgColor: 'bg-blue-500/10',
  },
  {
    id: 'gamma',
    name: 'Gamma (Γ)',
    symbol: 'Γ',
    range: 'Always ≥ 0, peaks ATM',
    definition: 'Rate of change of Delta per $1 move in the stock. Measures how fast Delta shifts.',
    interpretation: 'High gamma near ATM means delta changes rapidly — hedges need frequent rebalancing. Low gamma far ITM/OTM means delta is stable.',
    hedgeUse: 'Long options have positive gamma (good for hedge holders). Short options have negative gamma (risk for option sellers).',
    color: '#a855f7',
    bgColor: 'bg-purple-500/10',
  },
  {
    id: 'theta',
    name: 'Theta (Θ)',
    symbol: 'Θ',
    range: 'Usually negative for long options',
    definition: 'Daily time decay — how much the option loses per day, all else equal.',
    interpretation: 'A theta of -0.05 means the option loses $0.05/day. Accelerates as expiry approaches (theta bleed). Largest for ATM options.',
    hedgeUse: 'This is the cost of holding protective options. Selling options (covered calls) earns theta. Balance protection cost vs. decay.',
    color: '#ef4444',
    bgColor: 'bg-red-500/10',
  },
  {
    id: 'vega',
    name: 'Vega (ν)',
    symbol: 'ν',
    range: 'Always ≥ 0 for long options',
    definition: 'Change in option price per 1% change in implied volatility.',
    interpretation: 'A vega of 0.15 means the option gains $0.15 if IV rises by 1%. Long options benefit from rising IV; short options lose.',
    hedgeUse: 'Buy hedges when IV is low (cheap). Sell when IV is high (rich premiums). IV typically spikes before earnings and during crashes.',
    color: '#22c55e',
    bgColor: 'bg-green-500/10',
  },
];

// ─── Main Component ────────────────────────────────────────────────

export function GreeksExplainer() {
  const [spot, setSpot] = useState(100);
  const [strike, setStrike] = useState(100);
  const [vol, setVol] = useState(25);
  const [days, setDays] = useState(60);
  const [optType, setOptType] = useState<'put' | 'call'>('put');

  const T = days / 365;
  const sigma = vol / 100;

  const spotData = useMemo(() => greeksVsSpot(strike, T, sigma, optType), [strike, T, sigma, optType]);
  const timeData = useMemo(() => greeksVsTime(spot, strike, sigma, optType), [spot, strike, sigma, optType]);
  const volData = useMemo(() => greeksVsVol(spot, strike, T, optType), [spot, strike, T, optType]);

  const currentGreeks = useMemo(() => bsGreeks(spot, strike, T, 0.03, sigma, optType), [spot, strike, T, sigma, optType]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-primary/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            📐 The Greeks — Option Sensitivity Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            The Greeks measure how an option's price responds to changes in underlying price, time, and volatility.
            Adjust the parameters below — all charts and values update in real time using Black-Scholes.
          </p>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-5">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <GSlider label="Spot Price" value={spot} min={20} max={300} step={1} onChange={setSpot} suffix="$" />
            <GSlider label="Strike Price" value={strike} min={20} max={300} step={1} onChange={setStrike} suffix="$" />
            <GSlider label="IV" value={vol} min={5} max={80} step={1} onChange={setVol} suffix="%" />
            <GSlider label="Days to Expiry" value={days} min={1} max={365} step={1} onChange={setDays} suffix="d" />
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Option Type</Label>
              <div className="flex gap-2 mt-1">
                <button onClick={() => setOptType('put')} className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${optType === 'put' ? 'bg-red-500/20 text-red-700 dark:text-red-400 border border-red-500/30' : 'bg-muted/40 text-muted-foreground'}`}>
                  Put
                </button>
                <button onClick={() => setOptType('call')} className={`flex-1 text-xs py-1.5 rounded-md font-medium transition-colors ${optType === 'call' ? 'bg-green-500/20 text-green-700 dark:text-green-400 border border-green-500/30' : 'bg-muted/40 text-muted-foreground'}`}>
                  Call
                </button>
              </div>
            </div>
          </div>

          {/* Live values */}
          <div className="grid grid-cols-5 gap-3 mt-4">
            <div className="p-2 bg-background rounded-lg text-center border border-border">
              <p className="text-[10px] text-muted-foreground">Price</p>
              <p className="text-sm font-bold text-foreground">${currentGreeks.price.toFixed(2)}</p>
            </div>
            {greekInfo.map(g => {
              const val = currentGreeks[g.id as keyof typeof currentGreeks] as number;
              return (
                <div key={g.id} className={`p-2 rounded-lg text-center border border-border ${g.bgColor}`}>
                  <p className="text-[10px] text-muted-foreground">{g.symbol}</p>
                  <p className="text-sm font-bold" style={{ color: g.color }}>
                    {g.id === 'gamma' ? val.toFixed(4) : val.toFixed(3)}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Greek explanation cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {greekInfo.map(g => (
          <Card key={g.id} className={`${g.bgColor} border`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold" style={{ color: g.color }}>{g.name}</h3>
                <Badge variant="outline" className="text-[10px]">{g.range}</Badge>
              </div>
              <p className="text-sm text-foreground font-medium">{g.definition}</p>
              <p className="text-xs text-muted-foreground">{g.interpretation}</p>
              <div className="p-2 bg-background/60 rounded text-xs text-muted-foreground">
                <strong className="text-foreground">Hedge application:</strong> {g.hedgeUse}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts: Greeks vs Stock Price */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Greeks vs. Stock Price (Strike ${strike}, {days}d, IV {vol}%)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Delta & Gamma vs Price */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Delta & Gamma vs. Stock Price</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={spotData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="price" tick={{ fontSize: 10 }} label={{ value: 'Stock Price', position: 'insideBottom', offset: -5, style: { fontSize: 10 } }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Delta', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: 'Gamma', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <ReferenceLine x={strike} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" opacity={0.4} yAxisId="left" label={{ value: 'Strike', style: { fontSize: 9 } }} />
                  <Line yAxisId="left" type="monotone" dataKey="delta" stroke="#3b82f6" name="Delta" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="gamma" stroke="#a855f7" name="Gamma" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-1">Delta follows an S-curve; Gamma peaks at-the-money where delta changes fastest.</p>
            </div>

            {/* Theta & Vega vs Price */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Theta & Vega vs. Stock Price</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={spotData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="price" tick={{ fontSize: 10 }} label={{ value: 'Stock Price', position: 'insideBottom', offset: -5, style: { fontSize: 10 } }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Theta ($/day)', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: 'Vega ($/1%IV)', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <ReferenceLine x={strike} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" opacity={0.4} yAxisId="left" label={{ value: 'Strike', style: { fontSize: 9 } }} />
                  <Line yAxisId="left" type="monotone" dataKey="theta" stroke="#ef4444" name="Theta" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="vega" stroke="#22c55e" name="Vega" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-1">Both Theta and Vega peak ATM. Theta is your daily cost; Vega is your IV exposure.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts: Greeks vs Time */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Greeks vs. Time to Expiry (Spot ${spot}, Strike ${strike}, IV {vol}%)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Premium & Delta vs. Days to Expiry</h4>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={timeData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="days" reversed tick={{ fontSize: 10 }} label={{ value: 'Days to Expiry', position: 'insideBottom', offset: -5, style: { fontSize: 10 } }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Premium ($)', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Area yAxisId="left" type="monotone" dataKey="premium" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} name="Premium" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="delta" stroke="#94a3b8" name="Delta" strokeWidth={1.5} strokeDasharray="4 2" dot={false} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </AreaChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-1">Premium decays with time (theta). Delta converges to 0 or ±1 as expiry nears.</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Theta & Gamma vs. Days to Expiry</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={timeData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="days" reversed tick={{ fontSize: 10 }} label={{ value: 'Days to Expiry', position: 'insideBottom', offset: -5, style: { fontSize: 10 } }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Theta', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: 'Gamma', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Line yAxisId="left" type="monotone" dataKey="theta" stroke="#ef4444" name="Theta" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="gamma" stroke="#a855f7" name="Gamma" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-1">Theta accelerates near expiry. Gamma spikes ATM as expiry approaches — hedge rebalancing risk.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts: Greeks vs Volatility */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Greeks vs. Implied Volatility (Spot ${spot}, Strike ${strike}, {days}d)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Premium & Vega vs. IV</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={volData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="iv" tick={{ fontSize: 10 }} label={{ value: 'IV (%)', position: 'insideBottom', offset: -5, style: { fontSize: 10 } }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Premium ($)', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: 'Vega', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <ReferenceLine x={vol} stroke="hsl(var(--primary))" strokeDasharray="3 3" opacity={0.5} yAxisId="left" label={{ value: 'Current', style: { fontSize: 9 } }} />
                  <Line yAxisId="left" type="monotone" dataKey="premium" stroke="#3b82f6" name="Premium" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="vega" stroke="#22c55e" name="Vega" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-1">Premium rises near-linearly with IV. Buy hedges when IV is low for cheaper protection.</p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">Delta & Theta vs. IV</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={volData} margin={{ top: 5, right: 15, bottom: 15, left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="iv" tick={{ fontSize: 10 }} label={{ value: 'IV (%)', position: 'insideBottom', offset: -5, style: { fontSize: 10 } }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Delta', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: 'Theta', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <ReferenceLine x={vol} stroke="hsl(var(--primary))" strokeDasharray="3 3" opacity={0.5} yAxisId="left" label={{ value: 'Current', style: { fontSize: 9 } }} />
                  <Line yAxisId="left" type="monotone" dataKey="delta" stroke="#3b82f6" name="Delta" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="theta" stroke="#ef4444" name="Theta" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-1">Higher IV pushes OTM delta toward 0.5 and increases theta decay cost.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card className="bg-muted/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">📋 Greeks Quick Reference for Hedgers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 font-semibold text-foreground">Greek</th>
                  <th className="text-left py-2 px-3 font-semibold text-foreground">What it tells you</th>
                  <th className="text-left py-2 px-3 font-semibold text-foreground">Long Option</th>
                  <th className="text-left py-2 px-3 font-semibold text-foreground">Short Option</th>
                  <th className="text-left py-2 px-3 font-semibold text-foreground">Key Takeaway</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/30">
                  <td className="py-2 px-3 font-bold text-blue-600 dark:text-blue-400">Delta</td>
                  <td className="py-2 px-3 text-muted-foreground">$ change per $1 stock move</td>
                  <td className="py-2 px-3 text-green-700 dark:text-green-400">Positive (calls) / Negative (puts)</td>
                  <td className="py-2 px-3 text-red-700 dark:text-red-400">Opposite sign</td>
                  <td className="py-2 px-3 text-foreground">≈ probability of expiring ITM</td>
                </tr>
                <tr className="border-b border-border/30">
                  <td className="py-2 px-3 font-bold text-purple-600 dark:text-purple-400">Gamma</td>
                  <td className="py-2 px-3 text-muted-foreground">Rate delta changes</td>
                  <td className="py-2 px-3 text-green-700 dark:text-green-400">Positive (convexity benefit)</td>
                  <td className="py-2 px-3 text-red-700 dark:text-red-400">Negative (convexity risk)</td>
                  <td className="py-2 px-3 text-foreground">Peaks ATM, near expiry</td>
                </tr>
                <tr className="border-b border-border/30">
                  <td className="py-2 px-3 font-bold text-red-600 dark:text-red-400">Theta</td>
                  <td className="py-2 px-3 text-muted-foreground">Daily time decay</td>
                  <td className="py-2 px-3 text-red-700 dark:text-red-400">Negative (costs you)</td>
                  <td className="py-2 px-3 text-green-700 dark:text-green-400">Positive (earns for you)</td>
                  <td className="py-2 px-3 text-foreground">Accelerates near expiry</td>
                </tr>
                <tr className="border-b border-border/30">
                  <td className="py-2 px-3 font-bold text-green-600 dark:text-green-400">Vega</td>
                  <td className="py-2 px-3 text-muted-foreground">$ change per 1% IV move</td>
                  <td className="py-2 px-3 text-green-700 dark:text-green-400">Positive (benefits from IV rise)</td>
                  <td className="py-2 px-3 text-red-700 dark:text-red-400">Negative (hurt by IV rise)</td>
                  <td className="py-2 px-3 text-foreground">Buy when IV is low</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
